import React, { useState } from 'react';
import { PlusIcon, MoreVerticalIcon, PencilIcon, Trash2Icon, BriefcaseIcon } from 'lucide-react';
import { useServices, useCreateService, useUpdateService, useDeleteService } from '../../hooks/useServices';
import { ServiceFormModal } from './ServiceFormModal';
import { Toast } from '../Toast';
import type { Service, ServiceFormData } from '../../types/services';

function formatCurrency(amount: number | null): string {
  if (amount === null || amount === undefined) return '—';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(amount);
}

export function ServicesList() {
  const { data, isLoading, isError } = useServices();
  const createService = useCreateService();
  const deleteService = useDeleteService();

  const [showForm, setShowForm] = useState(false);
  const [editingService, setEditingService] = useState<Service | null>(null);
  const [activeMenuId, setActiveMenuId] = useState<string | null>(null);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const services = data?.services ?? [];

  const handleOpenForm = (service?: Service) => {
    if (service) {
      setEditingService(service);
    } else {
      setEditingService(null);
    }
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingService(null);
  };

  const handleSave = async (formData: ServiceFormData) => {
    if (editingService) {
      // For updates, we need to get the update mutation for this specific service
      const response = await fetch(`/api/profile/services/${editingService.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error('Failed to update service');
      }
      setToastMessage('Service updated');
    } else {
      await createService.mutateAsync(formData);
      setToastMessage('Service added');
    }
  };

  const handleDelete = async (serviceId: string) => {
    if (window.confirm('Are you sure you want to delete this service?')) {
      await deleteService.mutateAsync(serviceId);
      setToastMessage('Service deleted');
    }
    setActiveMenuId(null);
  };

  const toggleMenu = (serviceId: string) => {
    setActiveMenuId(activeMenuId === serviceId ? null : serviceId);
  };

  if (isLoading) {
    return (
      <div className="py-8 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#5B8E87]"></div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="py-4 text-center text-red-500">
        Failed to load services
      </div>
    );
  }

  return (
    <div>
      {services.length === 0 ? (
        <div className="py-8 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <BriefcaseIcon className="w-8 h-8 text-gray-400" />
          </div>
          <p className="text-gray-600 mb-4">No services defined yet</p>
          <button
            onClick={() => handleOpenForm()}
            className="inline-flex items-center gap-2 bg-[#5B8E87] text-white px-4 py-2 rounded-lg font-medium"
          >
            <PlusIcon className="w-4 h-4" />
            Add Service
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {services.map((service) => (
            <div
              key={service.id}
              className={`flex items-center justify-between p-4 rounded-xl border ${
                service.is_active
                  ? 'bg-white border-gray-200'
                  : 'bg-gray-50 border-gray-200 opacity-60'
              }`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h4 className="font-medium text-[#212121] truncate">
                    {service.name}
                  </h4>
                  {!service.is_active && (
                    <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-200 text-gray-600">
                      Inactive
                    </span>
                  )}
                </div>
                {service.description && (
                  <p className="text-sm text-gray-500 truncate mt-0.5">
                    {service.description}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-3 ml-4">
                <span className="text-lg font-semibold text-[#5B8E87]">
                  {formatCurrency(service.default_price)}
                </span>
                <div className="relative">
                  <button
                    onClick={() => toggleMenu(service.id)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <MoreVerticalIcon className="w-5 h-5 text-gray-500" />
                  </button>
                  {activeMenuId === service.id && (
                    <>
                      <div
                        className="fixed inset-0 z-40"
                        onClick={() => setActiveMenuId(null)}
                      />
                      <div className="absolute right-0 top-full mt-1 w-40 bg-white rounded-lg shadow-lg border border-gray-200 z-50 overflow-hidden">
                        <button
                          onClick={() => {
                            handleOpenForm(service);
                            setActiveMenuId(null);
                          }}
                          className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                        >
                          <PencilIcon className="w-4 h-4" />
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(service.id)}
                          className="w-full px-4 py-2.5 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                        >
                          <Trash2Icon className="w-4 h-4" />
                          Delete
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
          <button
            onClick={() => handleOpenForm()}
            className="w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed border-gray-300 rounded-xl text-gray-500 hover:border-[#5B8E87] hover:text-[#5B8E87] transition-colors"
          >
            <PlusIcon className="w-5 h-5" />
            Add Service
          </button>
        </div>
      )}

      <ServiceFormModal
        isOpen={showForm}
        onClose={handleCloseForm}
        onSave={handleSave}
        editingService={editingService}
        isSaving={createService.isPending}
      />

      {toastMessage && (
        <Toast message={toastMessage} onClose={() => setToastMessage(null)} />
      )}
    </div>
  );
}
